import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, MessageSquare, FileText, CreditCard, Settings,
  BarChart3, LogOut, Lock, Server, Activity, Globe, Megaphone, LineChart, Monitor,
  Trophy,
} from "lucide-react";

import AdminDashboardTab from "./admin/AdminDashboardTab";
import AdminContactsTab from "./admin/AdminContactsTab";
import AdminUsersTab from "./admin/AdminUsersTab";
import AdminBlogsTab from "./admin/AdminBlogsTab";
import AdminPaymentsTab from "./admin/AdminPaymentsTab";
import AdminAnalyticsTab from "./admin/AdminAnalyticsTab";
import AdminSEOTab from "./admin/AdminSEOTab";
import AdminSalesTab from "./admin/AdminSalesTab";
import AdminSystemTab from "./admin/AdminSystemTab";
import AdminLogsTab from "./admin/AdminLogsTab";
import AdminAnnouncementsTab from "./admin/AdminAnnouncementsTab";
import AdminPSEOTab from "./admin/AdminPSEOTab";
import AdminMonitoringTab from "./admin/AdminMonitoringTab";
import AdminCreatorIncentiveTab from "./admin/AdminCreatorIncentiveTab";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NAV_SECTIONS = [
  {
    label: "Main",
    items: [
      { id: "dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { id: "users", icon: Users, label: "User Management" },
      { id: "contacts", icon: MessageSquare, label: "Contact Messages" },
      { id: "blogs", icon: FileText, label: "Blog Manager" },
      { id: "creator_incentive", icon: Trophy, label: "Creator Incentives" },
    ],
  },
  {
    label: "Analytics & Sales",
    items: [
      { id: "sales", icon: LineChart, label: "Sales Analytics" },
      { id: "payments", icon: CreditCard, label: "Payments" },
      { id: "analytics", icon: BarChart3, label: "Traffic Analytics" },
    ],
  },
  {
    label: "System",
    items: [
      { id: "system", icon: Server, label: "System Health" },
      { id: "logs", icon: Activity, label: "Activity Logs" },
      { id: "announcements", icon: Megaphone, label: "Announcements" },
      { id: "seo", icon: Globe, label: "SEO Settings" },
      { id: "pseo", icon: Globe, label: "Programmatic SEO" },
      { id: "monitoring", icon: Monitor, label: "Monitoring" },
    ],
  },
];

const TAB_COMPONENTS = {
  dashboard: AdminDashboardTab,
  contacts: AdminContactsTab,
  users: AdminUsersTab,
  blogs: AdminBlogsTab,
  payments: AdminPaymentsTab,
  analytics: AdminAnalyticsTab,
  seo: AdminSEOTab,
  sales: AdminSalesTab,
  system: AdminSystemTab,
  logs: AdminLogsTab,
  announcements: AdminAnnouncementsTab,
  pseo: AdminPSEOTab,
  monitoring: AdminMonitoringTab,
  creator_incentive: AdminCreatorIncentiveTab,
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, {
        credentials: "include",
      });
      if (res.ok) {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error("Verify error:", error);
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      const data = await res.json();
      if (data.success) {
        setIsAuthenticated(true);
        toast.success("Login successful!");
      } else {
        toast.error(data.detail || "Login failed");
      }
    } catch (error) {
      toast.error("Login failed");
    }
    setLoginLoading(false);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, { method: "POST", credentials: "include" });
    } catch (error) { /* ignore */ }
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-600 to-purple-700 p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-violet-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
            <p className="text-gray-500 mt-2">MarkeeMix Admin Panel</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <Input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="admin@markeemix.com" className="mt-1" required data-testid="admin-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Password</label>
              <Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="••••••••" className="mt-1" required data-testid="admin-password-input" />
            </div>
            <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" disabled={loginLoading} data-testid="admin-login-button">
              {loginLoading ? "Logging in..." : "Login"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Button variant="ghost" onClick={() => navigate("/")}>Back to Website</Button>
          </div>
        </Card>
      </div>
    );
  }

  const ActiveTabComponent = TAB_COMPONENTS[activeTab];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="fixed left-0 top-0 h-full w-64 bg-gray-900 text-white p-4 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8 p-2">
          <div className="w-10 h-10 bg-violet-500 rounded-lg flex items-center justify-center">
            <Settings className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-bold">MarkeeMix</h1>
            <p className="text-xs text-gray-400">Admin Panel</p>
          </div>
        </div>

        <nav className="space-y-1" data-testid="admin-sidebar">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="text-xs text-gray-500 uppercase px-4 mb-2 mt-4">{section.label}</p>
              {section.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  data-testid={`nav-${item.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    activeTab === item.id
                      ? "bg-violet-600 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <Button variant="ghost" className="w-full text-gray-400 hover:text-white hover:bg-gray-800" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="ml-64 p-8">
        {ActiveTabComponent && <ActiveTabComponent apiUrl={API_URL} getAuthHeaders={getAuthHeaders} />}
      </div>
    </div>
  );
}
